import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { API_URL } from '../config';
import { toast } from 'react-toastify';

function PayoutHistory() {
    const [payouts, setPayouts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const navigate = useNavigate();
    const location = useLocation();

    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');
    const userEmail = localStorage.getItem('email');

    useEffect(() => {
        if (!token || role !== 'WORKER') {
            navigate('/');
            return;
        }
        fetchPayouts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    const fetchPayouts = async () => {
        setLoading(true);
        try {
            const res = await axios.get(
                `${API_URL}/api/worker/payouts?page=${page}&size=20`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const data = res.data.content || res.data;
            setPayouts(data);
            setTotalPages(res.data.totalPages || 1);
            setTotalElements(res.data.totalElements || data.length);
        } catch (err) {
            toast.error('Failed to load payouts');
        }
        setLoading(false);
    };

    const handleLogout = () => {
        localStorage.clear();
        navigate('/');
    };

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'PENDING': return 'badge badge-warning';
            case 'APPROVED': return 'badge badge-success';
            case 'COMPLETED': return 'badge badge-success';
            case 'REJECTED': return 'badge badge-error';
            default: return 'badge';
        }
    };

    return (
        <div className="dashboard-layout">
            {/* SIDEBAR */}
            <aside className="sidebar">
                <Link to="/" className="sidebar-brand">infloir</Link>
                <nav className="sidebar-nav">
                    <Link to="/worker-dashboard" className="nav-item">
                        <span>📊</span> Dashboard
                    </Link>
                    <Link to="/wallet" className="nav-item">
                        <span>💰</span> Wallet
                    </Link>
                    <Link to="/worker/transactions" className="nav-item">
                        <span>🧾</span> Transactions
                    </Link>
                    <Link to="/worker/payouts" className={`nav-item ${location.pathname === '/worker/payouts' ? 'active' : ''}`}>
                        <span>💸</span> Payouts
                    </Link>
                    <Link to="/profile" className="nav-item">
                        <span>👤</span> Profile
                    </Link>
                </nav>
                <div style={{ marginTop: 'auto' }}>
                    <button onClick={handleLogout} className="nav-item" style={{ width: '100%', background: 'transparent', border: 'none', textAlign: 'left', cursor: 'pointer' }}>
                        <span>🚪</span> Log out
                    </button>
                </div>
            </aside>

            {/* MAIN CONTENT */}
            <div className="main-content">
                <header className="topbar">
                    <h2 style={{ fontSize: '18px', fontWeight: '500' }}>Payout History</h2>
                    <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{userEmail}</span>
                </header>

                <main className="page-container page-fade-in">
                    {loading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
                            <div className="spinner" style={{ width: '24px', height: '24px', borderTopColor: '#fff' }}></div>
                        </div>
                    ) : payouts.length === 0 ? (
                        <div className="card" style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)' }}>
                            <p style={{ fontSize: '32px', marginBottom: '12px' }}>💸</p>
                            <p>No payout requests yet.</p>
                            <p style={{ fontSize: '13px', marginTop: '8px' }}>Request a payout from your wallet when you have balance.</p>
                        </div>
                    ) : (
                        <>
                            <div style={{ marginBottom: '16px' }}>
                                <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                                    Showing <strong style={{ color: 'var(--text)' }}>{totalElements}</strong> total payouts
                                </p>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' }}>
                                {payouts.map(p => (
                                    <div key={p.id} className="card">
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid var(--border)' }}>
                                            <span style={{ fontSize: '24px', fontWeight: '600', color: 'var(--text)', letterSpacing: '-0.03em' }}>
                                                ${parseFloat(p.amount).toFixed(2)}
                                            </span>
                                            <span className={getStatusBadgeClass(p.status)}>
                                                {p.status}
                                            </span>
                                        </div>
                                        
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <span>Requested</span>
                                                <span style={{ color: 'var(--text)' }}>{new Date(p.requestedAt).toLocaleString()}</span>
                                            </div>
                                            {p.processedAt && (
                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <span>Processed</span>
                                                    <span style={{ color: 'var(--text)' }}>{new Date(p.processedAt).toLocaleString()}</span>
                                                </div>
                                            )}
                                            {p.notes && (
                                                <div style={{ marginTop: '8px', padding: '12px', background: 'var(--bg-hover)', borderRadius: 'var(--radius-md)' }}>
                                                    <span style={{ display: 'block', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Notes</span>
                                                    <span style={{ color: 'var(--text)' }}>{p.notes}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', marginTop: '32px' }}>
                                    <button
                                        onClick={() => setPage(p => Math.max(0, p - 1))}
                                        disabled={page === 0}
                                        className="btn-secondary"
                                        style={{ padding: '8px 16px', fontSize: '13px' }}
                                    >
                                        ← Previous
                                    </button>
                                    <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                                        Page {page + 1} of {totalPages}
                                    </span>
                                    <button
                                        onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                                        disabled={page >= totalPages - 1}
                                        className="btn-secondary"
                                        style={{ padding: '8px 16px', fontSize: '13px' }}
                                    >
                                        Next →
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </main>
            </div>
        </div>
    );
}

export default PayoutHistory;